import { WithdrawMethodId } from '@starsoft/common/enums';
import { useWithdrawSearchParams } from '@starsoft/common/hooks';
import { parseToSubunits } from '@starsoft/common/utils';
import { useCallback } from 'react';
import { WithdrawFormCreateDto } from '../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { useWithdrawals } from '../useWithdrawals';
import { UseWithdrawalProps } from './props';

/**
 * Custom hook to handle withdrawals across different payment methods
 *
 * This hook consolidates the withdrawal functionality for multiple payment methods including:
 * - Crypto withdrawals
 * - Better Bro withdrawals (Brazilian PIX, Mexican SPEI, Indian IMPS, Argentine Bank Transfer)
 * - PIX To Pay withdrawals
 * - Pay Games withdrawals (India UPI, Indonesia QRIS, Nigeria, Singapore PayNow, South Africa)
 *
 * It manages the loading states and provides a unified interface to create withdrawals
 * based on the selected withdrawal method.
 *
 * @param {UseWithdrawalProps} options - Optional configuration object
 * @param {Function} [options.onSuccess] - Custom success callback function
 *
 * @returns {Object} An object containing:
 *   - withdraw: Function to create a withdrawal based on the selected method
 *     @param {WithdrawFormCreateDto} data - The withdrawal form data
 *   - isLoading: Boolean indicating if any withdrawal creation is in progress
 */
export default function useWithdrawal(options?: UseWithdrawalProps) {
  const { onSuccess } = options ?? {};
  const { withdrawMethodId } = useWithdrawSearchParams();
  const { isLoading: loadingCreateCryptoWithdraw, createWithdrawals } =
    useWithdrawals({
      onSuccess,
    });

  // Combine loading states from all withdrawal methods
  const isLoading: boolean = loadingCreateCryptoWithdraw;

  /**
   * Creates a withdrawal based on the selected withdrawal method
   *
   * @param {WithdrawFormCreateDto} data - The withdrawal form data containing:
   *   - address: Crypto wallet address (for crypto withdrawals)
   *   - amount: Withdrawal amount
   *   - chainId: Blockchain network ID (for crypto withdrawals)
   *   - code: Validation code
   *   - coinId: Cryptocurrency ID (for crypto withdrawals)
   *   - emailId: User's email ID (for crypto withdrawals)
   *   - paymentDetailsId: ID of saved payment details
   */
  const withdraw = useCallback(
    (data: WithdrawFormCreateDto) => {
      switch (withdrawMethodId) {
        case WithdrawMethodId.Crypto?.toString():
          createWithdrawals({
            amount: parseToSubunits(data?.amount, data?.coin),
            methodId: WithdrawMethodId.Crypto,
            coin: data?.coin,
            crypto: {
              address: data?.crypto?.address,
              chainId: data?.crypto?.chainId,
              code: data?.crypto?.code,
              coinId: data?.crypto?.coinId,
              emailId: data?.crypto?.emailId,
              amount: data?.crypto?.amount,
            },
          });
          break;
        default:
          createWithdrawals({
            amount: parseToSubunits(data?.amount, data?.coin),
            methodId: withdrawMethodId,
            paymentDetailsId: data?.paymentDetailsId,
          });
      }
    },
    [withdrawMethodId, createWithdrawals],
  );

  return {
    withdraw,
    isLoading,
  };
}
